import { Flex, Stack } from '@chakra-ui/react'

import FormContentBlock from './FormContentBlock'
import FormQuestionMetadata from './FormQuestionMetadata'
import { ROW_SIZE } from './helpers'
import type {
  FormBlock,
  FormBlocksComponentProps,
  FormErrors,
  FormQuestion,
  RenderQuestion,
} from './types'

function FormInputItem({
  input,
  error,
  renderQuestion,
}: {
  input: FormQuestion
  error?: string
  renderQuestion: RenderQuestion
}): React.ReactElement {
  return (
    <>
      {input.label != null ? <FormQuestionMetadata input={input} error={error} /> : null}
      <Flex direction="column" mt="12px">
        {renderQuestion(input)}
        {input.description == null ? null : (
          <Flex direction="column" mt={4}>
            <FormContentBlock content={input.description} />
          </Flex>
        )}
      </Flex>
    </>
  )
}

function FormRowVertical({
  blocks,
  errors,
  renderQuestion,
  alignInputs,
}: {
  blocks: FormBlock[]
  errors: FormErrors
  renderQuestion: RenderQuestion
  alignInputs?: 'center'
}): React.ReactElement | null {
  let sizeToFill = ROW_SIZE
  blocks.forEach(block => {
    sizeToFill -= block.size
  })

  return (
    <Stack
      direction={{ base: 'column', sm: 'row' }}
      spacing={{ base: '24px', sm: '24px' }}
      width="100%"
    >
      {blocks.map(block => (
        <Flex key={block.name} flex={block.sizeSpan ?? block.size} direction="row" width="100%">
          <Flex
            direction="column"
            flex={{ base: 'auto', md: block.size }}
            width={{ base: '100%', sm: 'auto' }}
            maxWidth="100%"
            align={alignInputs ?? undefined}
          >
            {block.display === 'display' ? (
              <FormContentBlock content={block.content} />
            ) : (
              <FormInputItem
                input={block}
                error={errors[block.name]}
                renderQuestion={renderQuestion}
              />
            )}
          </Flex>

          {block.sizeSpan != null && block.sizeSpan > 0 ? (
            <Flex flex={block.sizeSpan - block.size} height="10px" width="10px" />
          ) : null}
        </Flex>
      ))}
      {sizeToFill > 0 ? <Flex flex={sizeToFill} display={{ base: 'none', sm: 'flex' }} /> : null}
    </Stack>
  )
}

export default function FormBlocksVertical({
  rows,
  errors,
  renderQuestion,
  alignInputs,
}: FormBlocksComponentProps): React.ReactElement {
  return (
    <Stack direction="column" spacing="24px">
      {rows.map(row => (
        <FormRowVertical
          key={row.key}
          errors={errors}
          blocks={row.blocks}
          renderQuestion={renderQuestion}
          alignInputs={alignInputs}
        />
      ))}
    </Stack>
  )
}
